import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { CornerDownRight, LayoutTemplate, X } from 'lucide-react';
import {
  CHECKLIST_ITEM_MAX_LENGTH,
  TASK_DESCRIPTION_MAX_LENGTH,
  TASK_RECURRENCE_TEXTS,
  TASK_TEXTS,
  TASK_TITLE_MAX_LENGTH,
  TASK_TRASH_TEXTS,
  taskPriorities,
  taskPriorityLabels,
  taskStageLabels,
  taskStages,
  type TaskPriority,
  type TaskStage,
  NOTE_TASK_TEXTS,
  buildNoteTaskDraft,
  type QuickEntryPerson,
} from '@till-erp/collab/values';
import { absentOn, useAbsencesForDate } from '../lib/absence.ts';
import { apiFetch, ApiProblem, newIdempotencyKey, type Assignee, type StaffAbsent, type CreatedTask, type CreatedTaskFromNote, type TaskDetail, type TaskFieldsPatch, type TaskLabel, type TaskState, type TaskTemplateEntry } from '../lib/api.ts';
import { entityPath } from '../lib/entities.ts';
import { formatDate, formatDateTime, formatTime, isoDateInBerlin } from '../lib/format.ts';
import { useScreen } from '../lib/layout.ts';
import { useSession } from '../lib/session.tsx';
import { isLockedRecord } from '../lib/task-record.ts';
import { cn } from '../lib/utils.ts';
import { RecordPanel } from './record-panel.tsx';
import { DescriptionDraftButton, DescriptionDraftCard, TASK_DESCRIPTION_DRAFT_UI_TEXTS, useStartDescriptionDraft, useTaskDescriptionDraftAvailable } from './task-description-draft.tsx';
import { TaskChecklist } from './task-checklist.tsx';
import { RecurrenceChooser } from './task-recurrence.tsx';
import { LabelChooser, LabelSelect, TASK_LABEL_UI_TEXTS } from './task-labels.tsx';
import { TASK_TEMPLATE_UI_TEXTS, TemplatePicker } from './task-templates.tsx';
import { ProjectChooser, ProjectNote, TASK_PROJECT_UI_TEXTS } from './task-projects.tsx';
import { Sheet } from './sheet.tsx';
import { ArticleCombobox, CustomerCombobox, OrderCombobox } from './combobox-variants.tsx';
import type { UndoOffer } from './undo-toast.tsx';
import { Avatar } from './ui/avatar.tsx';
import { Button } from './ui/button.tsx';
import { Combobox, type ComboOption } from './ui/combobox.tsx';
import { DateField } from './ui/date-field.tsx';
import { Input } from './ui/input.tsx';
import { LockedRecord } from './ui/locked-record.tsx';
import { radioKeyTarget } from './ui/radio-keys.ts';
import { Segment } from './ui/segment.tsx';
import { SEG_TRACK, tabStateClass } from './ui/tab-style.ts';
import { Textarea } from './ui/textarea.tsx';

/** Texte des Detail-Blatts (Story 23.1, Entwurf Variante A „Linear-Panel“). */
export const TASK_DETAIL_TEXTS = {
  label: 'Aufgabe',
  newLabel: TASK_TEXTS.newTask,
  crumb: (by: string, at: string) => `Angelegt von ${by} · ${at}`,
  title: 'Titel',
  titlePlaceholder: 'Was ist zu tun?',
  stage: 'Status',
  assignee: 'Zuständig',
  priority: 'Priorität',
  due: 'Fällig',
  start: 'Start',
  startEmpty: 'Start festlegen',
  startClear: 'Start entfernen',
  record: 'Datensatz',
  noRecord: 'Freie Aufgabe – ohne Datensatz',
  recordKind: 'Art des Datensatzes',
  recordNone: 'Keiner',
  description: 'Beschreibung',
  descriptionPlaceholder: 'Details, Absprachen, nächste Schritte …',
  history: 'Verlauf',
  create: 'Anlegen',
  loading: 'Lädt …',
  today: 'heute',
  closedAutomatically: (note: string) => `Automatisch geschlossen: ${note}`,
  fromTemplate: (name: string) => `Aus Vorlage „${name}“`,
  clearTemplate: 'Leeren',
  clearTemplateLabel: 'Vorlage entfernen und Felder leeren',
  labels: 'Labels',
  checklist: 'Checkliste',
  checklistCount: (n: number) => TASK_TEMPLATE_UI_TEXTS.points(n),
  checklistPoint: (n: number) => `Punkt ${n}`,
  checklistRemove: (n: number) => `Punkt ${n} entfernen`,
  toNote: 'Zur Notiz',
  noteRecord: 'Wie die Notiz',
  descriptionFromNote: 'Beschreibung (aus der Notiz)',
  createFromNote: 'Aufgabe anlegen',
  cancel: 'Abbrechen',
} as const;

/** Notiz, aus der „Als Aufgabe …“ eine Aufgabe macht (Story 23.20). */
export interface NoteTaskSource {
  entryId: string;
  authorName: string;
  /** Zeitpunkt der Notiz (ISO). */
  at: string;
  body: string;
  entityType: string;
  entityId: string;
  /** Erwähnte Personen der Notiz (Deutung „@Name“). */
  mentioned: readonly QuickEntryPerson[];
  /** Art und Anzeigename des Datensatzes der Notiz, z. B. „Kunde“ + „20105 · Testfirma“ (aus der Chatter-Seite). */
  record?: { label: string; name: string };
}

/** Band „Aus Notiz von Anna Test, 22.02.2027“ mit Sprung zur Notiz (Neue Aufgabe und Detail-Blatt, Story 23.20). */
export function FromNoteBand({ authorName, at, entityType, entityId, entryId, onJump }: { authorName: string; at: string; entityType: string; entityId: string; entryId: string; onJump?: () => void }) {
  const path = entityPath(entityType, entityId);
  return (
    <div className="flex min-h-10 items-center gap-space-2 rounded-tile bg-brand-tile px-space-3 py-space-1 text-row text-brand-tile-fg" data-testid="aus-notiz-band">
      <CornerDownRight size={14} strokeWidth={2} aria-hidden className="flex-none" />
      <span className="min-w-0 flex-1 truncate">{NOTE_TASK_TEXTS.fromNote(authorName, formatDate(isoDateInBerlin(new Date(at))))}</span>
      {path ? (
        <Link
          to={path}
          hash={`eintrag-${entryId}`}
          onClick={onJump}
          className="inline-flex min-h-9 items-center rounded-field px-space-2 font-medium underline-offset-2 hover:underline pointer-coarse:min-h-11"
          data-testid="zur-notiz"
        >
          {TASK_DETAIL_TEXTS.toNote}
        </Link>
      ) : null}
    </div>
  );
}

/** Wochentag kurz für „Mo 22.02.2027 · heute“. */
const WEEKDAYS = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

function weekdayDate(date: string): string {
  return `${WEEKDAYS[new Date(`${date}T12:00:00Z`).getUTCDay()]} ${formatDate(date)}`;
}

/** `2027-03-01` → „01.03.“ (Toast „Erscheint ab TT.MM.“). */
export function dayMonth(date: string): string {
  return `${date.slice(8, 10)}.${date.slice(5, 7)}.`;
}

function problemText(err: unknown): string {
  return err instanceof ApiProblem ? (Object.values(err.errors)[0] ?? err.detail) : 'Etwas ist schiefgelaufen.';
}

// ---------------------------------------------------------------------------
// Marken: Status-Symbol und Prioritäts-Pille (auch in der Aufgabenzeile)

/**
 * Status-Symbol einer Aufgabe (Entwurf Variante A): Offen = Ring, In Arbeit = halb gefüllt, Wartet = gestrichelter Ring,
 * Erledigt = gefüllt mit Haken. Rein zeichnerisch (`aria-hidden`); das Wort steht daneben bzw. im Namen des Knopfs.
 */
export function StageMark({ stage, className }: { stage: TaskStage; className?: string }) {
  const tone = stage === 'in_progress' ? 'text-brand' : stage === 'done' ? 'text-ready' : stage === 'waiting' ? 'text-ink-2' : 'text-ink-3';
  return (
    <svg aria-hidden data-stage-mark={stage} viewBox="0 0 16 16" width={16} height={16} className={cn('flex-none', tone, className)}>
      {stage === 'done' ? (
        <>
          <circle cx="8" cy="8" r="7" fill="currentColor" />
          <path d="M4.8 8.2 7 10.4l4.2-4.4" fill="none" stroke="var(--card)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </>
      ) : (
        <>
          <circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray={stage === 'waiting' ? '2.6 2.3' : undefined} />
          {stage === 'in_progress' ? <path d="M8 3.5a4.5 4.5 0 0 1 0 9Z" fill="currentColor" /> : null}
        </>
      )}
    </svg>
  );
}

/** Farben der Prioritäten (Pille, Karte, Schnell-Erfassung) – einzige Stelle. */
export const PRIORITY_LOOK: Record<TaskPriority, { pill: string; dot: string }> = {
  urgent: { pill: 'bg-clarify-bg text-clarify', dot: 'bg-clarify' },
  high: { pill: 'bg-check-bg text-check', dot: 'bg-check-dot' },
  normal: { pill: 'bg-fill text-ink-2', dot: 'border-[1.5px] border-ink-3' },
  low: { pill: 'bg-fill text-ink-2', dot: 'bg-ink-3' },
};

/**
 * Prioritäts-Pille (EXPERIENCE §4.22 „Priorität“): Dringend rot, Hoch bernstein, Niedrig grau, je mit Punkt und Wort aus
 * der Statuspalette (nie Farbe allein). Die Liste zeigt „Normal“ nicht (`hideNormal`), das Blatt schon.
 */
export function PriorityPill({ priority, className }: { priority: TaskPriority; className?: string }) {
  const look = PRIORITY_LOOK[priority];
  return (
    <span data-priority={priority} className={cn('type-meta inline-flex h-7 flex-none items-center gap-space-2 rounded-full px-space-3 whitespace-nowrap', look.pill, className)}>
      <span aria-hidden className={cn('size-2 flex-none rounded-full', look.dot)} />
      {taskPriorityLabels[priority]}
    </span>
  );
}

/**
 * Auswahl der Priorität im Blatt (Sichtung 28.10.2026): Klick auf die Pille öffnet alle vier Stufen mit ihrer Farbe als
 * Radiogruppe (Pfeiltasten wie das Status-Segment); Wahl speichert sofort und schließt.
 */
export function PriorityChooser({
  value,
  onChange,
  busy,
  expanded = false,
}: {
  value: TaskPriority;
  onChange: (p: TaskPriority) => void;
  busy: boolean;
  /** Story 23.20 (Entwurf A „Aus Notiz“): Umschalter Niedrig · Normal · Hoch · Dringend immer offen, ohne Pille davor. */
  expanded?: boolean;
}) {
  const [openState, setOpen] = useState(false);
  const open = expanded || openState;
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const trigger = useRef<HTMLButtonElement>(null);
  const groupId = useId();
  // Offen als Umschalter aufsteigend wie Entwurf A (Niedrig → Dringend); als Auswahl unter der Pille wie bisher.
  const options = expanded ? [...taskPriorities] : [...taskPriorities].reverse();
  const index = Math.max(0, options.indexOf(value));
  const choose = (p: TaskPriority) => {
    if (!expanded) {
      setOpen(false);
      trigger.current?.focus();
    }
    if (p !== value) onChange(p);
  };
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && !expanded) {
      e.stopPropagation();
      setOpen(false);
      trigger.current?.focus();
      return;
    }
    const focused = refs.current.findIndex((r) => r === document.activeElement);
    const next = radioKeyTarget(e.key, focused < 0 ? index : focused, options.length);
    if (next === null) return;
    e.preventDefault();
    refs.current[next]?.focus();
  };
  return (
    <div className="flex flex-col items-start gap-space-2">
      {expanded ? null : (
        <button
          ref={trigger}
          type="button"
          aria-expanded={open}
          aria-controls={open ? groupId : undefined}
          aria-label={`${TASK_DETAIL_TEXTS.priority}: ${taskPriorityLabels[value]}`}
          disabled={busy}
          onClick={() => setOpen((v) => !v)}
          className="-mx-space-2 inline-flex min-h-9 cursor-pointer items-center rounded-field px-space-2 hover:bg-tile disabled:cursor-default pointer-coarse:min-h-11"
        >
          <PriorityPill priority={value} />
        </button>
      )}
      {open ? (
        <div id={groupId} role="radiogroup" aria-label={TASK_DETAIL_TEXTS.priority} onKeyDown={onKeyDown} className={cn(SEG_TRACK, 'max-w-full flex-wrap')} data-testid="prioritaet-wahl">
          {options.map((p, i) => (
            <button
              key={p}
              ref={(el) => {
                refs.current[i] = el;
              }}
              type="button"
              role="radio"
              aria-checked={p === value}
              tabIndex={p === value ? 0 : -1}
              autoFocus={!expanded && p === value}
              onClick={() => choose(p)}
              // Aktive Wahl wie jede Umschalt-Leiste dunkelblau (tab-style); die Pille behält ihre Farbe.
              className={cn('cursor-pointer rounded-full pointer-coarse:min-h-11', expanded ? 'p-space-1 pointer-coarse:p-0' : 'p-space-1', tabStateClass(p === value, { seg: true }))}
            >
              {/* Ausgeklappt (Notiz-Blatt): nur die gewählte Stufe hervorgehoben, die übrigen ohne eigene Fläche. */}
              <PriorityPill priority={p} className={expanded ? cn('pointer-coarse:px-space-2', p !== value && 'bg-transparent') : undefined} />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rahmen: Seitenpanel ab Tablet, Blatt von unten am Handy

function DetailFrame({ label, title, recordKey, onClose, children }: { label: string; title: ReactNode; recordKey: string; onClose: () => void; children: ReactNode }) {
  const screen = useScreen();
  const opener = useRef<HTMLElement | null>(typeof document === 'undefined' ? null : (document.activeElement as HTMLElement | null));
  if (screen === 'phone') {
    return (
      <Sheet label={label} opener={opener} onClose={onClose} className="h-[86vh]">
        <div data-testid="aufgabe-blatt" className="flex flex-col gap-space-5 pb-space-4">
          {children}
        </div>
      </Sheet>
    );
  }
  return (
    <RecordPanel title={title} label={label} recordKey={recordKey} onClose={onClose} width="panel">
      <div data-testid="aufgabe-blatt" className="flex flex-col gap-space-5">
        {children}
      </div>
    </RecordPanel>
  );
}

/** Eigenschaften-Zeile: Bezeichnung links (12/ink-3), Wert rechts. */
function Prop({ label, htmlFor, children }: { label: string; htmlFor?: string; children: ReactNode }) {
  return (
    <>
      <dt className="flex min-h-11 items-center text-body text-ink-3">{htmlFor ? <label htmlFor={htmlFor}>{label}</label> : label}</dt>
      <dd className="m-0 flex min-h-11 min-w-0 items-center">{children}</dd>
    </>
  );
}

/**
 * Optionaler Datensatz beim Anlegen (Story 23.1: „optional Datensatz (Kunde, Auftrag, Artikel über die Suche)“): Art
 * wählen, dann über die globale Suche finden (Auswahl mit Suche, Ticket 1.257). Mit Datensatz ist es eine Wiedervorlage.
 */
const RECORD_KINDS = [
  { value: 'none', label: TASK_DETAIL_TEXTS.recordNone },
  { value: 'customer', label: 'Kunde' },
  { value: 'order', label: 'Auftrag' },
  { value: 'article', label: 'Artikel' },
] as const;
type RecordKind = (typeof RECORD_KINDS)[number]['value'];
/** Aus einer Notiz an einer Art ohne Suche (z. B. Angebot, Vorgang): diese Art als eigene Wahl „wie die Notiz“. */
type PickKind = RecordKind | 'note';

function isRecordKind(kind: string): kind is Exclude<RecordKind, 'none'> {
  return RECORD_KINDS.some((k) => k.value === kind && k.value !== 'none');
}

const PROPS = 'grid grid-cols-[var(--container-field-xs)_minmax(0,1fr)] items-center gap-x-space-4 gap-y-space-1';

/**
 * Auswahl „Zuständig“ mit Hinweis „abwesend bis 26.02.“, wenn die Person am Fälligkeitstag abwesend ist (Story 23.9,
 * [RUDOLF 22.10.2026, A3]): nur der Satz, nie Art oder Grund; Zuweisen bleibt erlaubt.
 */
function assigneeOptionsOf(items: readonly Assignee[] | undefined, absences: StaffAbsent['people'], dueDate: string | null): ComboOption[] {
  return (items ?? []).map((p) => {
    const hint = absentOn(absences, p.id, dueDate);
    return hint ? { value: p.id, label: p.displayName, hint } : { value: p.id, label: p.displayName };
  });
}

export function useAssignees() {
  const { getToken } = useSession();
  return useQuery({ queryKey: ['assignees'], queryFn: () => apiFetch<{ items: Assignee[] }>(getToken, '/api/v1/tasks/assignees') });
}

// ---------------------------------------------------------------------------
// Neue Aufgabe

/** Punkt der Checkliste vor dem Anlegen (Story 23.12); `key` hält die Zeile beim Entfernen stabil. */
interface DraftPoint {
  key: string;
  text: string;
}

let draftPointSeq = 0;
const draftPoint = (text: string): DraftPoint => ({ key: `p${(draftPointSeq += 1)}`, text });

/**
 * Checkliste aus der Vorlage im Blatt „Neue Aufgabe“: Punkte ändern oder entfernen, bevor die Aufgabe angelegt wird.
 * Leere Punkte werden beim Anlegen weggelassen. Sortieren und Abhaken erst an der angelegten Aufgabe.
 */
function DraftChecklist({ points, onChange }: { points: DraftPoint[]; onChange: (points: DraftPoint[]) => void }) {
  if (points.length === 0) return null;
  return (
    <section aria-label={TASK_DETAIL_TEXTS.checklist} className="flex flex-col gap-space-2" data-testid="neu-checkliste">
      <span className="text-caption text-ink-3">
        {TASK_DETAIL_TEXTS.checklist} · <span data-testid="neu-checkliste-anzahl">{TASK_DETAIL_TEXTS.checklistCount(points.length)}</span>
      </span>
      <ol className="m-0 flex list-none flex-col p-0">
        {points.map((p, i) => (
          <li key={p.key} className="group flex min-h-10 items-center gap-space-2 border-b border-line last:border-b-0" data-testid="neu-punkt">
            <span aria-hidden className="w-5 flex-none text-right text-caption text-ink-3 tabular-nums">
              {i + 1}
            </span>
            <Input
              value={p.text}
              maxLength={CHECKLIST_ITEM_MAX_LENGTH}
              aria-label={TASK_DETAIL_TEXTS.checklistPoint(i + 1)}
              onChange={(e) => onChange(points.map((x) => (x.key === p.key ? { ...x, text: e.target.value } : x)))}
              className="h-9 min-w-0 flex-1 border-transparent bg-transparent px-space-2 hover:border-line focus:border-field-line max-tablet:h-11 max-tablet:text-field-phone"
            />
            <button
              type="button"
              aria-label={TASK_DETAIL_TEXTS.checklistRemove(i + 1)}
              title={TASK_DETAIL_TEXTS.checklistRemove(i + 1)}
              onClick={() => onChange(points.filter((x) => x.key !== p.key))}
              className="grid size-8 flex-none cursor-pointer place-items-center rounded-field text-ink-3 opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 hover:text-ink focus-visible:opacity-100 pointer-coarse:size-11 pointer-coarse:opacity-100"
              data-testid="neu-punkt-entfernen"
            >
              <X size={16} strokeWidth={2} aria-hidden />
            </button>
          </li>
        ))}
      </ol>
    </section>
  );
}

/**
 * „Neue Aufgabe“ (Story 23.1): Titel, Zuständig (vorbelegt: ich), Fällig (vorbelegt: heute bzw. `initialDueDate`), Priorität. „Anlegen“ legt eine
 * freie Aufgabe an; danach zeigt das Blatt sie (die Seite wechselt auf die neue Aufgabe).
 */
export function NewTaskSheet({
  today,
  initialDueDate,
  initial,
  project,
  fromNote,
  onClose,
  onCreated,
}: {
  today: string;
  /** Vorbelegtes Fälligkeitsdatum (z. B. „+“ an einem Tag im Kalender, Story 23.6); sonst heute. */
  initialDueDate?: string;
  /** Vorbelegung aus der Schnell-Erfassung („Mehr Felder …“, Story 23.14); ohne Angabe die üblichen Startwerte. */
  initial?: {
    title: string;
    dueDate: string;
    priority: TaskPriority;
    assigneeId: string | null;
    /** Erkannte Labels und Datensatz aus der Zeile (Projekt kommt über `project`). */
    labels?: TaskLabel[];
    record?: { kind: Exclude<RecordKind, 'none'>; id: string } | null;
  };
  /** „+ Aufgabe“ auf der Projektseite (Story 23.7): neue Aufgabe gehört gleich zu diesem Projekt. */
  project?: { id: string; name: string };
  /**
   * „Als Aufgabe …“ an einer Notiz (Story 23.20): Titel, Zuständig, Fällig und Priorität aus der festen Deutung
   * (`buildNoteTaskDraft`), Beschreibung = Notiztext, Datensatz = der der Notiz; Band „Aus Notiz von …“. Gespeichert über
   * `POST /api/v1/chatter/{entryId}/task`; `existing` = es gab sie schon (keine zweite).
   */
  fromNote?: NoteTaskSource;
  onClose: () => void;
  onCreated: (task: CreatedTask & { existing?: boolean }) => void;
}) {
  const { getToken, me } = useSession();
  const screen = useScreen();
  const staffRole = me.role === 'mitarbeiter';
  const assignees = useAssignees();
  // Entwurf aus der Notiz einmal beim Öffnen (danach frei änderbar).
  const [noteDraft] = useState(() => (fromNote ? buildNoteTaskDraft({ body: fromNote.body, today, mentioned: fromNote.mentioned, self: me.id }) : null));
  const start = noteDraft ? { title: noteDraft.title, assigneeId: noteDraft.assigneeId, dueDate: noteDraft.dueDate, priority: noteDraft.priority } : initial;
  const [title, setTitle] = useState(start?.title ?? '');
  const [assigneeId, setAssigneeId] = useState<string | null>(start ? start.assigneeId : me.id);
  const [dueDate, setDueDate] = useState(start?.dueDate ?? initialDueDate ?? today);
  // Hinweis „abwesend bis …“ am gewählten Fälligkeitstag – auch überfällig oder weit in der Zukunft (eigene Abfrage).
  const absences = useAbsencesForDate(dueDate);
  const [priority, setPriority] = useState<TaskPriority>(start?.priority ?? 'normal');
  // Aus einer Notiz (Story 23.20): vorbelegt mit dem Datensatz der Notiz, vor dem Anlegen änderbar oder entfernbar.
  const noteKind: PickKind | null = fromNote ? (isRecordKind(fromNote.entityType) ? fromNote.entityType : 'note') : null;
  const [recordKind, setRecordKind] = useState<PickKind>(staffRole ? 'none' : (noteKind ?? initial?.record?.kind ?? 'none'));
  const [recordId, setRecordId] = useState<string | null>(
    staffRole ? null : fromNote ? (noteKind === 'note' ? null : fromNote.entityId) : (initial?.record?.id ?? null),
  );
  const recordKinds: readonly { value: PickKind; label: string }[] =
    noteKind === 'note' ? [...RECORD_KINDS, { value: 'note', label: fromNote?.record?.label ?? TASK_DETAIL_TEXTS.noteRecord }] : RECORD_KINDS;
  // Anzeigename des vorbelegten Datensatzes, solange er nicht neu gesucht wurde.
  const noteRecordLabel = fromNote && recordKind === fromNote.entityType && recordId === fromNote.entityId ? (fromNote.record?.name ?? null) : null;
  /** Gewählte Vorlage (Story 23.12): Labels, Checkliste und Beschreibung vorbelegt, vor dem Anlegen änderbar, dann kopiert. */
  const [template, setTemplate] = useState<{ id: string; name: string } | null>(null);
  const [labels, setLabels] = useState<TaskLabel[]>(initial?.labels ?? []);
  const draftLabels = (initial?.labels?.length ?? 0) > 0;
  const [checklist, setChecklist] = useState<DraftPoint[]>([]);
  const [description, setDescription] = useState(fromNote?.body ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ids = { title: useId(), assignee: useId(), due: useId(), record: useId(), description: useId() };
  const applyTemplate = (t: TaskTemplateEntry) => {
    setTemplate({ id: t.id, name: t.name });
    setLabels(t.labels);
    setChecklist(t.checklist.map(draftPoint));
    setDescription(t.description ?? '');
    setTitle(t.title);
    setPriority(t.priority);
    setDueDate(t.dueDate);
    // Standard-Zuständig nur, wenn die Person wählbar ist; sonst wer anlegt.
    const known = t.defaultAssigneeId !== null && (assignees.data?.items ?? []).some((p) => p.id === t.defaultAssigneeId);
    setAssigneeId(known ? t.defaultAssigneeId : me.id);
    setError(null);
  };
  const clearTemplate = () => {
    setTemplate(null);
    setLabels([]);
    setChecklist([]);
    setDescription('');
    setTitle('');
    setPriority('normal');
    setDueDate(initialDueDate ?? today);
    setAssigneeId(me.id);
    setError(null);
  };
  const recordMissing = recordKind !== 'none' && recordKind !== 'note' && recordId === null;
  const submit = async () => {
    if (title.trim() === '' || !assigneeId || !dueDate || recordMissing || busy) return;
    setBusy(true);
    setError(null);
    if (fromNote) {
      try {
        const created = await apiFetch<CreatedTaskFromNote>(getToken, `/api/v1/chatter/${fromNote.entryId}/task`, {
          method: 'POST',
          body: {
            title: title.trim(),
            assigneeId,
            dueDate,
            priority,
            description: description.trim() === '' ? null : description,
            // Datensatz unverändert = der der Notiz (ohne Angabe); „Keiner“ = freie Aufgabe; sonst der gewählte.
            ...(recordKind === 'none'
              ? { record: null }
              : recordKind === 'note' || (recordKind === fromNote.entityType && recordId === fromNote.entityId) || !recordId
                ? {}
                : { record: { entityType: recordKind, entityId: recordId } }),
          },
          idempotencyKey: newIdempotencyKey(),
        });
        onCreated(created);
      } catch (err) {
        setError(problemText(err));
      } finally {
        setBusy(false);
      }
      return;
    }
    try {
      const created = await apiFetch<CreatedTask>(getToken, '/api/v1/tasks', {
        method: 'POST',
        body: {
          title: title.trim(),
          assigneeId,
          dueDate,
          priority,
          ...(template
            ? {
                templateId: template.id,
                checklist: checklist.map((p) => p.text.trim()).filter((t) => t !== ''),
                labelIds: labels.map((l) => l.id),
                description: description.trim() === '' ? null : description,
              }
            : labels.length > 0
              ? { labelIds: labels.map((l) => l.id) }
              : {}),
          ...(recordKind !== 'none' && recordId ? { entityType: recordKind, entityId: recordId } : {}),
          ...(project ? { projectId: project.id } : {}),
        },
        idempotencyKey: newIdempotencyKey(),
      });
      onCreated(created);
    } catch (err) {
      setError(problemText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <DetailFrame label={TASK_DETAIL_TEXTS.newLabel} title={TASK_DETAIL_TEXTS.newLabel} recordKey="new" onClose={onClose}>
      <form
        className="flex flex-col gap-space-5"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        {/* „Aus Vorlage …“ für alle (Story 23.12); danach das Band „Aus Vorlage „…“ · Leeren“. Aus einer Notiz: Band „Aus Notiz von …“. */}
        {fromNote ? (
          <FromNoteBand authorName={fromNote.authorName} at={fromNote.at} entityType={fromNote.entityType} entityId={fromNote.entityId} entryId={fromNote.entryId} onJump={onClose} />
        ) : template ? (
          <div className="flex min-h-10 items-center gap-space-2 rounded-tile bg-brand-tile px-space-3 py-space-1 text-row text-brand-tile-fg" data-testid="aus-vorlage-band">
            <LayoutTemplate size={14} strokeWidth={2} aria-hidden className="flex-none" />
            <span className="min-w-0 flex-1 truncate">{TASK_DETAIL_TEXTS.fromTemplate(template.name)}</span>
            <button
              type="button"
              aria-label={TASK_DETAIL_TEXTS.clearTemplateLabel}
              onClick={clearTemplate}
              className="inline-flex min-h-9 cursor-pointer items-center rounded-field px-space-2 font-medium underline-offset-2 hover:underline pointer-coarse:min-h-11"
              data-testid="aus-vorlage-leeren"
            >
              {TASK_DETAIL_TEXTS.clearTemplate}
            </button>
          </div>
        ) : (
          <TemplatePicker onChoose={applyTemplate} className="self-start" />
        )}
        <label className="flex flex-col gap-space-2">
          <span className="text-body text-ink-2">{TASK_DETAIL_TEXTS.title}</span>
          <Input
            id={ids.title}
            value={title}
            maxLength={TASK_TITLE_MAX_LENGTH}
            placeholder={TASK_DETAIL_TEXTS.titlePlaceholder}
            onChange={(e) => setTitle(e.target.value)}
            // Aus einer Notiz ist alles vorbelegt: am Handy keine Tastatur aufklappen (Hauptaktion bleibt sichtbar).
            autoFocus={!(fromNote && screen === 'phone')}
            className="h-11 w-full px-space-4 max-tablet:text-field-phone"
            data-testid="neu-titel"
          />
        </label>
        <dl className={cn(PROPS, 'm-0')}>
          <Prop label={TASK_DETAIL_TEXTS.assignee} htmlFor={ids.assignee}>
            <Combobox
              id={ids.assignee}
              label={TASK_DETAIL_TEXTS.assignee}
              value={assigneeId}
              options={assigneeOptionsOf(assignees.data?.items, absences, dueDate)}
              selectedLabel={assigneeId === me.id ? me.displayName : null}
              loading={assignees.isPending}
              onChange={(v) => setAssigneeId(v)}
              width="m"
            />
          </Prop>
          <Prop label={TASK_DETAIL_TEXTS.due} htmlFor={ids.due}>
            <DateField id={ids.due} value={dueDate} onChange={setDueDate} today={today} />
          </Prop>
          {fromNote ? (
            // Sichtung HS54 30.10.: aus einer Notiz Priorität über die volle Breite, Beschriftung darüber, alle vier Stufen in einer Zeile.
            <>
              <dt className="col-span-full pt-space-2 text-body text-ink-3">{TASK_DETAIL_TEXTS.priority}</dt>
              <dd className="col-span-full m-0 min-w-0">
                <PriorityChooser value={priority} onChange={setPriority} busy={busy} expanded />
              </dd>
            </>
          ) : (
            <Prop label={TASK_DETAIL_TEXTS.priority}>
              <PriorityChooser value={priority} onChange={setPriority} busy={busy} />
            </Prop>
          )}
          {template || draftLabels ? (
            <Prop label={TASK_DETAIL_TEXTS.labels}>
              <div className="py-space-1">
                <LabelSelect labels={labels} onChange={setLabels} testId="neu-labels" menuTestId="neu-label-wahl" />
              </div>
            </Prop>
          ) : null}
          {project ? (
            <Prop label={TASK_PROJECT_UI_TEXTS.project}>
              <span className="py-space-1" data-testid="neu-projekt">
                <ProjectNote name={project.name} />
              </span>
            </Prop>
          ) : null}
          {/* Story 23.17: Rolle Mitarbeiter liest keine Datensätze – keine Wahl eines Bezugs (immer freie Aufgabe). Aus einer Notiz (23.20): vorbelegt mit dem Bezug der Notiz, änderbar. */}
          {staffRole ? null : (
            <Prop label={TASK_DETAIL_TEXTS.record}>
              <div className="flex min-w-0 flex-col items-start gap-space-2 py-space-1">
                <Segment<PickKind>
                  label={TASK_DETAIL_TEXTS.recordKind}
                  testId="aufgabe-datensatz-art"
                  dense
                  options={recordKinds}
                  value={recordKind}
                  onChange={(kind) => {
                    setRecordKind(kind);
                    // Zurück zur Art der Notiz: wieder ihr Datensatz; sonst neu suchen.
                    setRecordId(fromNote && kind === fromNote.entityType ? fromNote.entityId : null);
                  }}
                />
                {recordKind === 'customer' ? <CustomerCombobox id={ids.record} value={recordId} onChange={setRecordId} selectedLabel={noteRecordLabel} width="m" /> : null}
                {recordKind === 'order' ? <OrderCombobox id={ids.record} value={recordId} onChange={setRecordId} selectedLabel={noteRecordLabel} width="m" /> : null}
                {recordKind === 'article' ? <ArticleCombobox id={ids.record} value={recordId} onChange={setRecordId} selectedLabel={noteRecordLabel} width="m" /> : null}
                {recordKind === 'note' && fromNote ? (
                  <span className="text-row text-ink-2" data-testid="neu-datensatz-notiz">
                    {fromNote.record ? `${fromNote.record.label} ${fromNote.record.name}` : TASK_DETAIL_TEXTS.noteRecord}
                  </span>
                ) : null}
              </div>
            </Prop>
          )}
        </dl>
        {fromNote ? (
          <div className="flex flex-col gap-space-2">
            <label htmlFor={ids.description} className="text-caption text-ink-3">
              {TASK_DETAIL_TEXTS.descriptionFromNote}
            </label>
            <Textarea
              id={ids.description}
              autoGrow
              value={description}
              maxLength={TASK_DESCRIPTION_MAX_LENGTH}
              onChange={(e) => setDescription(e.target.value)}
              className="max-tablet:text-field-phone"
              data-testid="neu-beschreibung"
            />
          </div>
        ) : null}
        {template ? (
          <>
            <DraftChecklist points={checklist} onChange={setChecklist} />
            <div className="flex flex-col gap-space-2">
              <label htmlFor={ids.description} className="text-caption text-ink-3">
                {TASK_DETAIL_TEXTS.description}
              </label>
              <Textarea
                id={ids.description}
                autoGrow
                value={description}
                maxLength={TASK_DESCRIPTION_MAX_LENGTH}
                placeholder={TASK_DETAIL_TEXTS.descriptionPlaceholder}
                onChange={(e) => setDescription(e.target.value)}
                className="max-tablet:text-field-phone"
                data-testid="neu-beschreibung"
              />
            </div>
          </>
        ) : null}
        {error ? (
          <p role="alert" className="text-body text-clarify">
            {error}
          </p>
        ) : null}
        {fromNote ? (
          // Am Handy klebt die Leiste unten im Blatt: „Aufgabe anlegen“ ist ohne Scrollen und ohne Tastatur erreichbar.
          <div className="flex justify-end gap-space-2 max-tablet:sticky max-tablet:bottom-0 max-tablet:-mx-space-4 max-tablet:border-t max-tablet:border-line max-tablet:bg-card max-tablet:px-space-4 max-tablet:py-space-3">
            <Button variant="secondary" onClick={onClose} className="max-tablet:flex-1">
              {TASK_DETAIL_TEXTS.cancel}
            </Button>
            <Button type="submit" disabled={busy || title.trim() === '' || !assigneeId || !dueDate} className="max-tablet:flex-1" data-testid="aus-notiz-anlegen">
              {TASK_DETAIL_TEXTS.createFromNote}
            </Button>
          </div>
        ) : (
          <div className="flex justify-end">
            <Button type="submit" disabled={busy || title.trim() === '' || !assigneeId || !dueDate || recordMissing}>
              {TASK_DETAIL_TEXTS.create}
            </Button>
          </div>
        )}
      </form>
    </DetailFrame>
  );
}

// ---------------------------------------------------------------------------
// Detail-Blatt einer Aufgabe

export const taskDetailKey = (id: string) => ['tasks', 'detail', id] as const;

/**
 * Detail-Blatt (Story 23.1, Entwurf Variante A; EXPERIENCE §4.22 „Detail-Blatt“): Titel zum Anklicken, Status als Segment
 * Offen · In Arbeit · Wartet · Erledigt, Eigenschaften (Zuständig, Priorität, Fällig, Start, Datensatz), Beschreibung und
 * Verlauf; Labels (Story 23.4) in den Eigenschaften, Checkliste (Story 23.3) unter der Beschreibung. Jede Änderung wird sofort gespeichert; danach 10 s „Rückgängig“ (`offerUndo`).
 */
export function TaskDetailSheet({
  id,
  today,
  onClose,
  onChanged,
  onDelete,
  offerUndo,
}: {
  id: string;
  today: string;
  onClose: () => void;
  /** Nach jeder Änderung: Liste neu laden. */
  onChanged: () => Promise<unknown>;
  /** „Löschen“ (Papierkorb, [RUDOLF] 29.10.2026): nur gezeigt, wenn die Person die Aufgabe löschen darf. */
  onDelete?: (task: TaskDetail) => Promise<void>;
  offerUndo: (o: UndoOffer) => void;
}) {
  const { getToken, me } = useSession();
  const queryClient = useQueryClient();
  const detail = useQuery({ queryKey: taskDetailKey(id), queryFn: () => apiFetch<TaskDetail>(getToken, `/api/v1/tasks/${id}`) });
  const assignees = useAssignees();
  const absences = useAbsencesForDate(detail.data?.dueDate);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [titleDraft, setTitleDraft] = useState<string | null>(null);
  const [descriptionDraft, setDescriptionDraft] = useState<string | null>(null);
  /** Für „Übernehmen“ der KI-Karte: steht nach dem Speichern noch ungespeicherter eigener Text im Feld? */
  const descriptionDraftRef = useRef(descriptionDraft);
  /** KI-Entwurf wird gerade übernommen: Beschreibungsfeld gesperrt, sonst ginge währenddessen Getipptes verloren. */
  const [aiAccepting, setAiAccepting] = useState(false);
  useEffect(() => {
    descriptionDraftRef.current = descriptionDraft;
  }, [descriptionDraft]);
  // Story 23.21: KI-Entwurf der Beschreibung (nur Vorschlag, AD-16); `aiDraft` = laufender Entwurf, `aiDraftProblem` = ruhige Meldung.
  const aiDraftAvailable = useTaskDescriptionDraftAvailable();
  const startAiDraft = useStartDescriptionDraft();
  const [aiDraft, setAiDraft] = useState<string | null>(null);
  const [aiDraftStarting, setAiDraftStarting] = useState(false);
  const [aiDraftProblem, setAiDraftProblem] = useState<string | null>(null);
  const ids = { title: useId(), description: useId(), assignee: useId(), due: useId(), start: useId(), project: useId() };
  const task = detail.data;

  const post = useCallback(<T,>(path: string, body: unknown) => apiFetch<T>(getToken, path, { method: 'POST', body, idempotencyKey: newIdempotencyKey() }), [getToken]);
  /** Neuer Stand sofort ins Blatt (Version für die nächste Änderung), danach Blatt und Liste neu laden. */
  const apply = useCallback(
    (s: TaskState) => {
      queryClient.setQueryData<TaskDetail>(taskDetailKey(id), (old) => (old ? { ...old, ...s } : old));
    },
    [queryClient, id],
  );
  const reload = useCallback(async () => {
    await Promise.all([onChanged(), queryClient.invalidateQueries({ queryKey: taskDetailKey(id) })]);
  }, [onChanged, queryClient, id]);

  /**
   * Änderungen nacheinander speichern (Warteschlange), je mit der zuletzt gespeicherten Version: Ein Klick auf Status oder
   * Priorität, der das Speichern von Titel/Beschreibung (beim Verlassen des Felds) auslöst, geht so nicht verloren.
   */
  const queue = useRef<Promise<boolean>>(Promise.resolve(true));
  /** Text, der gerade gespeichert wird (je Feld), gegen doppeltes Senden beim erneuten Verlassen des Felds. */
  const savingText = useRef<{ title?: string; description?: string | null }>({});
  const change = (path: 'fields' | 'stage', body: TaskFieldsPatch, revert: TaskFieldsPatch, text: string, opts: { text?: boolean; settled?: () => void } = {}) => {
    const run = () => save(path, body, revert, text, opts.text === true, opts.settled);
    const next = queue.current.then(run, run);
    queue.current = next;
    return next;
  };
  /**
   * Eine Änderung speichern und „Rückgängig“ anbieten (stellt den vorigen Wert gegen die neue Version wieder her).
   * `true` = gespeichert; sonst steht der Fehler im Blatt.
   */
  const save = async (
    path: 'fields' | 'stage',
    body: TaskFieldsPatch,
    revert: TaskFieldsPatch,
    text: string,
    textField: boolean,
    settled?: () => void,
  ): Promise<boolean> => {
    const version = queryClient.getQueryData<TaskDetail>(taskDetailKey(id))?.version;
    if (version === undefined) {
      settled?.();
      return false;
    }
    // Textfelder sperren Status und Priorität nicht (ihr Klick wartet in der Warteschlange).
    if (!textField) setBusy(true);
    setError(null);
    try {
      const s = await post<TaskState>(`/api/v1/tasks/${id}/${path}`, { ...body, version });
      apply(s);
      offerUndo({
        text,
        // Auch „Rückgängig“ in die Warteschlange (Zweit-Review PR #1474): nie parallel zu einem laufenden Speichern
        // derselben Version; kam inzwischen eine Änderung dazu, antwortet der Server mit „inzwischen geändert“.
        undo: () => {
          const run = async () => {
            const r = await post<TaskState>(`/api/v1/tasks/${id}/${path}`, { ...revert, version: s.version });
            apply(r);
            await reload();
          };
          const next = queue.current.then(run, run);
          queue.current = next.then(
            () => true,
            () => false,
          );
          return next;
        },
      });
      return true;
    } catch (err) {
      setError(problemText(err));
      return false;
    } finally {
      if (!textField) setBusy(false);
      // Antwort ist da: Sperre gegen doppeltes Senden sofort lösen, nicht erst nach dem Neuladen. Sonst speichert ein
      // erneutes Verlassen des Felds direkt nach „Etwas ist schiefgelaufen“ denselben Text nicht noch einmal.
      settled?.();
      await reload();
    }
  };

  if (!task) {
    return (
      <DetailFrame label={TASK_DETAIL_TEXTS.label} title={TASK_DETAIL_TEXTS.label} recordKey={id} onClose={onClose}>
        <p className={detail.error ? 'text-clarify' : 'text-ink-3'}>{detail.error ? problemText(detail.error) : TASK_DETAIL_TEXTS.loading}</p>
      </DetailFrame>
    );
  }

  // Story 23.17: gesperrter Bezug (Rolle Mitarbeiter) – Schloss + Art und Nummer, kein Link zum Datensatz.
  const locked = isLockedRecord(task);
  const path = !locked && task.entityType && task.entityId ? entityPath(task.entityType, task.entityId) : null;
  const closedForGood = task.stage === 'done' && task.closeNote !== null;
  const title = titleDraft ?? task.title;
  const description = descriptionDraft ?? task.description ?? '';
  // Entwurf erst nach erfolgreichem Speichern verwerfen (Zweit-Review PR #1474): schlägt es fehl (Netz, 409), bleibt der
  // getippte Text stehen; inzwischen weiter Getipptes bleibt ebenfalls.
  const saveTitle = () => {
    const next = title.trim();
    if (next === '' || next === task.title) {
      setTitleDraft(null);
      return;
    }
    // Derselbe Text wird schon gespeichert (erneutes Verlassen des Felds): nicht doppelt senden.
    if (savingText.current.title === next) return;
    savingText.current.title = next;
    const release = () => {
      if (savingText.current.title === next) delete savingText.current.title;
    };
    // Freigabe nur einmal, in `settled` (läuft in `save` immer, auch ohne Version): ein späteres Lösen könnte sonst die
    // Sperre eines inzwischen gestarteten neuen Versuchs mit demselben Text entfernen (Zweit-Review #1695).
    void change('fields', { title: next }, { title: task.title }, TASK_TEXTS.titleSet, { text: true, settled: release }).then((ok) => {
      if (ok) setTitleDraft((d) => (d === null || d.trim() === next ? null : d));
    });
  };
  const saveDescription = () => {
    const typed = description;
    const next = typed.trim() === '' ? null : typed;
    if (next === task.description) {
      setDescriptionDraft(null);
      return;
    }
    if (savingText.current.description === next) return;
    savingText.current.description = next;
    const release = () => {
      if (savingText.current.description === next) delete savingText.current.description;
    };
    void change('fields', { description: next }, { description: task.description }, next === null ? TASK_TEXTS.descriptionCleared : TASK_TEXTS.descriptionSet, {
      text: true,
      settled: release,
    }).then((ok) => {
      if (ok) setDescriptionDraft((d) => (d === null || d === typed ? null : d));
    });
  };
  const assigneeOptions = assigneeOptionsOf(assignees.data?.items, absences, task.dueDate);
  const crumb = TASK_DETAIL_TEXTS.crumb(task.createdByName, formatDate(isoDateInBerlin(new Date(task.createdAt))));

  return (
    <DetailFrame label={TASK_DETAIL_TEXTS.label} title={TASK_DETAIL_TEXTS.label} recordKey={id} onClose={onClose}>
      {/* Story 23.20: aus einer Notiz angelegt – Band mit Sprung zur Notiz (bei gesperrtem Bezug kein Band). */}
      {task.sourceNote ? (
        <FromNoteBand
          authorName={task.sourceNote.authorName}
          at={task.sourceNote.at}
          entityType={task.sourceNote.entityType}
          entityId={task.sourceNote.entityId}
          entryId={task.sourceNote.entryId}
        />
      ) : null}
      <div className="flex flex-col gap-space-1">
        <span className="text-caption text-ink-3" data-testid="aufgabe-herkunft">
          {crumb}
        </span>
        <label htmlFor={ids.title} className="sr-only">
          {TASK_DETAIL_TEXTS.title}
        </label>
        {/* Langer Titel bricht um statt abgeschnitten zu werden (eine Zeile, wächst mit dem Text); Enter speichert. */}
        <Textarea
          id={ids.title}
          autoGrow
          rows={1}
          value={title}
          maxLength={TASK_TITLE_MAX_LENGTH}
          disabled={busy && titleDraft === null}
          onChange={(e) => setTitleDraft(e.target.value)}
          onBlur={saveTitle}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.currentTarget.blur();
            }
            if (e.key === 'Escape' && titleDraft !== null) {
              e.stopPropagation();
              setTitleDraft(null);
            }
          }}
          data-testid="aufgabe-titel"
          className="-mx-space-2 min-h-0 w-[calc(100%+var(--spacing-space-4))] resize-none border-transparent bg-transparent px-space-2 py-space-1 text-watermark-sm leading-[1.2] font-semibold tracking-[-0.02em] text-ink hover:bg-hover hover:enabled:not-focus:border-transparent focus:bg-card disabled:border-transparent disabled:text-ink"
        />
      </div>

      <Segment<TaskStage>
        label={TASK_DETAIL_TEXTS.stage}
        testId="aufgabe-status"
        options={taskStages.map((s) => ({ value: s, label: taskStageLabels[s], disabled: closedForGood && s !== 'done' }))}
        value={task.stage}
        busy={busy}
        onChange={(stage) => {
          if (stage === task.stage) return;
          void change('stage', { stage }, { stage: task.stage }, TASK_TEXTS.stageSet(taskStageLabels[stage]));
        }}
        className="grid w-full grid-cols-4 max-tablet:overflow-x-auto"
      />
      {closedForGood ? <p className="text-body text-ink-3">{TASK_DETAIL_TEXTS.closedAutomatically(task.closeNote!)}</p> : null}

      <dl className={cn(PROPS, 'm-0')}>
        <Prop label={TASK_DETAIL_TEXTS.assignee} htmlFor={ids.assignee}>
          <span className="flex min-w-0 items-center gap-space-2">
            {task.assigneeName ? <Avatar name={task.assigneeName} className="size-6 text-micro" /> : null}
            <Combobox
              id={ids.assignee}
              label={TASK_DETAIL_TEXTS.assignee}
              value={task.assigneeId}
              options={assigneeOptions}
              selectedLabel={task.assigneeName}
              loading={assignees.isPending}
              disabled={busy || task.stage === 'done'}
              appearance="cell"
              onChange={(value, option) => {
                if (!value || value === task.assigneeId) return;
                void change('fields', { assigneeId: value }, { assigneeId: task.assigneeId }, TASK_TEXTS.assigneeSet(option?.label ?? (value === me.id ? me.displayName : '')));
              }}
            />
          </span>
        </Prop>
        <Prop label={TASK_DETAIL_TEXTS.priority}>
          <PriorityChooser
            value={task.priority}
            busy={busy}
            onChange={(priority) => void change('fields', { priority }, { priority: task.priority }, TASK_TEXTS.prioritySet(taskPriorityLabels[priority]))}
          />
        </Prop>
        <Prop label={TASK_DETAIL_TEXTS.due} htmlFor={ids.due}>
          <span className="flex flex-wrap items-center gap-x-space-3 gap-y-space-1">
            <DateField
              id={ids.due}
              value={task.dueDate}
              today={today}
              disabled={busy || task.stage === 'done'}
              aria-label={`${TASK_DETAIL_TEXTS.due} (${weekdayDate(task.dueDate)})`}
              onChange={(dueDate) => {
                if (!dueDate || dueDate === task.dueDate) return;
                void change('fields', { dueDate }, { dueDate: task.dueDate }, TASK_TEXTS.dueSet(formatDate(dueDate)));
              }}
            />
            {task.dueDate === today ? <TodayMark /> : task.overdue ? <span className="text-body text-clarify">überfällig</span> : null}
          </span>
        </Prop>
        {/* Wiederholen (Story 23.11, Entwurf A): nur freie Aufgaben – Wiedervorlagen am Datensatz legt das Modul an. */}
        {task.entityType === null ? (
          <Prop label={TASK_RECURRENCE_TEXTS.label}>
            <RecurrenceChooser taskId={id} dueDate={task.dueDate} recurrence={task.recurrence ?? null} onSaved={reload} offerUndo={offerUndo} />
          </Prop>
        ) : null}
        <Prop label={TASK_DETAIL_TEXTS.start} htmlFor={ids.start}>
          <span className="flex flex-wrap items-center gap-x-space-3 gap-y-space-1">
            <DateField
              id={ids.start}
              value={task.startDate ?? ''}
              today={today}
              disabled={busy}
              aria-label={task.startDate ? TASK_DETAIL_TEXTS.start : TASK_DETAIL_TEXTS.startEmpty}
              onChange={(startDate) => {
                const next = startDate === '' ? null : startDate;
                if (next === task.startDate) return;
                void change('fields', { startDate: next }, { startDate: task.startDate }, next ? TASK_TEXTS.startSet(formatDate(next)) : TASK_TEXTS.startCleared);
              }}
            />
            {task.startDate ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => void change('fields', { startDate: null }, { startDate: task.startDate }, TASK_TEXTS.startCleared)}
                className="cursor-pointer text-body text-link underline-offset-2 hover:underline pointer-coarse:min-h-11"
              >
                {TASK_DETAIL_TEXTS.startClear}
              </button>
            ) : null}
          </span>
        </Prop>
        <Prop label={TASK_LABEL_UI_TEXTS.section}>
          <LabelChooser
            taskId={id}
            labels={task.labels}
            offerUndo={offerUndo}
            onSaved={async (labels) => {
              queryClient.setQueryData<TaskDetail>(taskDetailKey(id), (old) => (old ? { ...old, labels } : old));
              await reload();
            }}
          />
        </Prop>
        {/* Story 23.7: Projekt nur für Wiedervorlagen und freie Aufgaben (Server: sonst 422). */}
        {task.group === 'follow_up' ? (
          <Prop label={TASK_PROJECT_UI_TEXTS.project} htmlFor={ids.project}>
            <ProjectChooser
              id={ids.project}
              taskId={id}
              project={task.project ?? null}
              disabled={busy}
              offerUndo={offerUndo}
              onSaved={async (project) => {
                queryClient.setQueryData<TaskDetail>(taskDetailKey(id), (old) => (old ? { ...old, project } : old));
                await reload();
              }}
            />
          </Prop>
        ) : null}
        <Prop label={TASK_DETAIL_TEXTS.record}>
          {locked ? (
            <LockedRecord entityType={task.entityType} entityLabel={task.entityLabel} entityName={task.entityName} className="text-row text-ink-2" />
          ) : path ? (
            <Link to={path} className="text-row text-link underline-offset-2 hover:underline">
              {`${task.entityLabel ?? ''} ${task.entityName ?? ''}`.trim()}
            </Link>
          ) : (
            <span className="text-row text-ink-3">{task.entityType ? `${task.entityLabel ?? ''} ${task.entityName ?? ''}`.trim() : TASK_DETAIL_TEXTS.noRecord}</span>
          )}
        </Prop>
      </dl>

      <div className="flex flex-col gap-space-2 border-t border-line pt-space-4">
        <div className="flex min-h-7 items-center justify-between gap-space-2">
          <label htmlFor={ids.description} className="text-caption text-ink-3">
            {TASK_DETAIL_TEXTS.description}
          </label>
          {aiDraftAvailable && !closedForGood && aiDraft === null ? (
            <DescriptionDraftButton
              disabled={aiDraftStarting}
              onClick={() => {
                setAiDraftStarting(true);
                setAiDraftProblem(null);
                // Erst die laufende Speicherung (Verlassen des Felds) abwarten, sonst ginge der alte Text an die KI; scheitert
                // sie und steht noch eigener Text ungespeichert im Feld, steht der Fehler im Blatt und es startet kein Entwurf (Zweit-Review #1584).
                queue.current
                  .then((saved) => (saved || descriptionDraftRef.current === null ? startAiDraft(id) : null))
                  .then((draftId) => {
                    if (draftId !== null) setAiDraft(draftId);
                  })
                  .catch(() => setAiDraftProblem(TASK_DESCRIPTION_DRAFT_UI_TEXTS.failed))
                  .finally(() => setAiDraftStarting(false));
              }}
            />
          ) : null}
        </div>
        <Textarea
          id={ids.description}
          autoGrow
          value={description}
          maxLength={TASK_DESCRIPTION_MAX_LENGTH}
          placeholder={TASK_DETAIL_TEXTS.descriptionPlaceholder}
          onChange={(e) => setDescriptionDraft(e.target.value)}
          onBlur={saveDescription}
          readOnly={aiAccepting}
          aria-busy={aiAccepting || undefined}
          className="max-tablet:text-field-phone"
        />
        {aiDraft ? (
          <DescriptionDraftCard
            key={aiDraft}
            draftId={aiDraft}
            onDone={() => setAiDraft(null)}
            onAccepting={setAiAccepting}
            // Gescheitertes Speichern blockiert nur, solange eigener Text ungespeichert im Feld steht (Zweit-Review #1584).
            waitForSaves={() => queue.current.then((ok) => ok || descriptionDraftRef.current === null)}
            onAccepted={async () => {
              const before = queryClient.getQueryData<TaskDetail>(taskDetailKey(id))?.description ?? null;
              setDescriptionDraft(null);
              await reload();
              // Wie jede Änderung im Blatt: 10 s „Rückgängig“ stellt die vorige Beschreibung wieder her.
              offerUndo({
                text: TASK_TEXTS.descriptionSet,
                undo: () => {
                  const run = async () => {
                    const version = queryClient.getQueryData<TaskDetail>(taskDetailKey(id))?.version;
                    if (version === undefined) return;
                    apply(await post<TaskState>(`/api/v1/tasks/${id}/fields`, { description: before, version }));
                    await reload();
                  };
                  const next = queue.current.then(run, run);
                  queue.current = next.then(
                    () => true,
                    () => false,
                  );
                  return next;
                },
              });
            }}
          />
        ) : null}
        {aiDraftProblem ? (
          <p role="status" className="text-body text-ink-3" data-testid="beschreibung-ki-problem">
            {aiDraftProblem}
          </p>
        ) : null}
      </div>

      <TaskChecklist taskId={id} items={task.checklistItems} stageDone={task.stage === 'done'} onChanged={reload} offerUndo={offerUndo} />

      {error ? (
        <p role="alert" className="text-body text-clarify">
          {error}
        </p>
      ) : null}

      <section aria-label={TASK_DETAIL_TEXTS.history} className="flex flex-col gap-space-2 border-t border-line pt-space-4" data-testid="aufgabe-verlauf">
        <span className="text-caption text-ink-3">{TASK_DETAIL_TEXTS.history}</span>
        <ol className="m-0 flex list-none flex-col gap-space-2 p-0">
          {task.history.map((h) => (
            <li key={h.id} className="text-body text-ink-2">
              <span className="text-ink">{h.authorName}</span> {h.text} <span className="text-ink-3">· {isoDateInBerlin(new Date(h.at)) === today ? formatTime(h.at) : formatDateTime(h.at)}</span>
            </li>
          ))}
        </ol>
      </section>

      {onDelete && task.canDelete ? (
        <div className="flex border-t border-line pt-space-4">
          <Button
            variant="secondary"
            size="sm"
            disabled={busy}
            onClick={() => void onDelete(task)}
            className="text-clarify"
            data-testid="aufgabe-loeschen"
          >
            {TASK_TRASH_TEXTS.delete}
          </Button>
        </div>
      ) : null}
    </DetailFrame>
  );
}

/** „heute“ hervorgehoben (Sichtung 28.10.2026): zarte Akzent-Fläche, Wort halbfett. */
export function TodayMark() {
  return <span className="rounded-full bg-brand-tile px-space-2 text-body font-medium text-brand-tile-fg">{TASK_DETAIL_TEXTS.today}</span>;
}
